/**
 * HLOS-323: TS-side bridge para STT nativo iOS via SFSpeechRecognizer.
 *
 * Substitui o Web Speech API no iOS WKWebView (que tem issues conhecidos).
 * Streama transcrições parciais via callbacks de completion.
 *
 * No Android/Web, retorna isAvailable()=false; o caller deve usar Web Speech.
 */
import { Capacitor } from "@capacitor/core";
import { installHlosRouterOnce, registerSpeechHandler } from "./hlosRouter";

export interface SpeechTranscription {
  text: string;
  isFinal: boolean;
}

export interface SpeechResult {
  state: "idle" | "listening" | "stopping" | "failed" | "denied";
  text?: string;
  isFinal?: boolean;
}

export type SpeechListener = (r: SpeechResult) => void;

export class AppleSpeechBridge {
  private isRequesting = false;
  private isStarting = false;
  private currentStartId: string | null = null;
  private currentListener: SpeechListener | null = null;
  private installed = false;

  isAvailable(): boolean {
    if (typeof window === "undefined") return false;
    const w = window as unknown as { webkit?: { messageHandlers?: { hlos?: unknown } } };
    return Capacitor.getPlatform() === "ios" && !!w.webkit?.messageHandlers?.hlos;
  }

  /** HLOS-325: instala handler de Speech (spk_*) no router compartilhado. */
  private install(): void {
    if (this.installed) return;
    installHlosRouterOnce();
    registerSpeechHandler((raw) => {
      const r = raw as {
        id?: string;
        ok?: boolean;
        result?: { granted?: boolean; text?: string; isFinal?: boolean; state?: string };
      };
      if (r.id !== this.currentStartId) return;
      // resposta de requestAuth (uma vez)
      if (r.result && "granted" in r.result) {
        this.currentListener?.({ state: r.ok ? "idle" : "denied" });
        return;
      }
      // chunks parciais
      if (r.result?.text !== undefined) {
        this.currentListener?.({
          state: "listening",
          text: r.result.text,
          isFinal: r.result.isFinal === true,
        });
        return;
      }
      // mudanças de estado
      if (r.result?.state) {
        const s = r.result.state as SpeechResult["state"];
        this.currentListener?.({ state: s });
        if (s === "idle" || s === "failed" || s === "denied") {
          this.currentStartId = null;
        }
      }
    });
    this.installed = true;
  }

  /** Pede autorização para microfone + reconhecimento. */
  async requestAuth(): Promise<boolean> {
    if (!this.isAvailable()) return false;
    if (this.isRequesting) return false;
    this.isRequesting = true;
    this.install();
    try {
      const w = window as unknown as { webkit: { messageHandlers: { hlos: { postMessage: (m: unknown) => void } } } };
      const id = `spk_auth_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      return new Promise<boolean>((resolve) => {
        const timer = setTimeout(() => {
          resolve(false);
        }, 5000);
        // listener one-shot via router: armazenamos o callback
        const prev = this.currentStartId;
        this.currentStartId = id;
        this.currentListener = (r) => {
          if (r.state === "denied" || r.state === "failed") {
            clearTimeout(timer);
            this.currentStartId = prev;
            resolve(false);
          } else if (r.state === "idle" && id === this.currentStartId) {
            // requestAuth respondeu — sem granted, não é "true"
            clearTimeout(timer);
            this.currentStartId = prev;
            resolve(false);
          }
        };
        w.webkit.messageHandlers.hlos.postMessage({
          id,
          method: "speechRequestAuth",
        });
      });
    } finally {
      this.isRequesting = false;
    }
  }

  /**
   * Inicia reconhecimento. onResult é chamado para cada chunk parcial
   * E para mudanças de estado.
   */
  async start(
    lang: string = "pt-BR",
    onResult: SpeechListener,
  ): Promise<void> {
    if (!this.isAvailable()) {
      onResult({ state: "denied", text: "" });
      return;
    }
    if (this.isStarting) return;
    this.isStarting = true;
    this.install();
    this.currentListener = onResult;
    const w = window as unknown as { webkit: { messageHandlers: { hlos: { postMessage: (m: unknown) => void } } } };
    this.currentStartId = `spk_start_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    w.webkit.messageHandlers.hlos.postMessage({
      id: this.currentStartId,
      method: "speechStart",
      lang,
    });
  }

  stop(): void {
    if (!this.isAvailable()) return;
    const w = window as unknown as { webkit: { messageHandlers: { hlos: { postMessage: (m: unknown) => void } } } };
    w.webkit.messageHandlers.hlos.postMessage({
      id: `spk_stop_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      method: "speechStop",
    });
    // HLOS-374 inv-B: NÃO limpar currentListener/currentStartId aqui.
    // Swift chama recognitionTask.finish() que dispara callback async com
    // o chunk final (id=spk_start_xxx, isFinal=true). Se limpamos os IDs
    // aqui, o handler filtra o chunk final → draft nunca atualizado →
    // transcrição perdida. Limpar apenas no listener handler quando estado
    // terminal (idle/failed/denied) chega.
  }
}

export const appleSpeechBridge = new AppleSpeechBridge();
